/**
 * «Prueba con el Sandbox» (SPEC-estadisticas-conexion §A.6). Opcional y colapsada.
 *
 * El Sandbox de Twilio NO sirve para campañas (sin plantillas propias, la sesión vence a los 3 días,
 * 1 mensaje cada 3 s). Sí sirve para probar, antes de tener el número propio, que el token anda,
 * que Twilio llega a PUBLIC_BASE_URL con una firma válida y que vuelven los recibos y las
 * respuestas. No toca campañas, saldo interno ni no contactar.
 *
 * Después de «Mandarme un mensaje de prueba» se pregunta el estado cada 2 s durante 60 s.
 *
 * Si la marca está frenada por `cumplimiento` (un envío dio 20003 con el token bueno) y la prueba sale después del
 * freno, Twilio ya deja mandar: se dice acá mismo y se ofrece reanudar la marca (`ReanudarTrasPrueba`). La cuenta se
 * relee para que la tarjeta de la marca frenada y el aviso de arriba lo digan también.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { CheckCircle2, ChevronDown, ChevronRight, Copy, FlaskConical, Loader2, Send } from "lucide-react";
import type { CuentaPublica, EstadoSandbox, ResultadoPruebaSandbox } from "../../types";
import { oficialApi } from "../../api";
import { useCuentaOficial } from "../../contexto";
import { usePolling } from "../../hooks/usePolling";
import {
  NUMERO_SANDBOX, frenoSuperado, sandboxTerminado, textoCuentaAtada, textoErrorConexion, textoErrorSandbox, textoFalloPruebaSandbox,
} from "../../logic/conexion";
import { validarCredencialesLocal } from "../../logic/config";
import { leerError, leerRespuesta } from "../../logic/respuesta";
import { Tarjeta } from "../Tarjeta";
import { ReanudarTrasPrueba } from "./ReanudarTrasPrueba";
import { fechaHora } from "../../../campana-nueva/utils/format";
import { activaApi } from "../../../campana-nueva/api";

const POLLING_MS = 2000;
const TOPE_MS = 60_000;
const E164 = /^\+\d{8,15}$/;

const ESTADO_SANDBOX: Record<string, string> = {
  queued: "en cola en Twilio", accepted: "aceptado por Twilio", sending: "saliendo", sent: "enviado",
  delivered: "entregado ✓✓", read: "leído ✓✓", failed: "falló", undelivered: "no se entregó",
};

export function PruebaSandbox() {
  const { cuenta, recargar } = useCuentaOficial();
  const [abierta, setAbierta] = useState(false);
  /** lo que devolvió la última prueba: trae `frenoSuperado` antes de que la cuenta se relea */
  const [resultado, setResultado] = useState<ResultadoPruebaSandbox | null>(null);
  const [estado, setEstado] = useState<EstadoSandbox | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [to, setTo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [desde, setDesde] = useState<number | null>(null);
  const [copiado, setCopiado] = useState("");
  const montado = useRef(true);
  useEffect(() => () => { montado.current = false; }, []);

  const traer = useCallback(async (): Promise<EstadoSandbox | null> => {
    const l = leerRespuesta<EstadoSandbox>(await oficialApi.sandbox());
    if (!l.ok) throw Object.assign(new Error(textoErrorConexion(l)), { textoListo: true });
    if (montado.current && l.data) setEstado(l.data);
    return l.data ?? null;
  }, []);

  useEffect(() => {
    if (!abierta || estado) return;
    setCargando(true);
    traer()
      .catch((e) => {
        // Un rechazo 422 ya viene con su texto (lo arma `traer`); lo que lanza req() se traduce acá.
        const listo = e instanceof Error && (e as Error & { textoListo?: boolean }).textoListo === true;
        setError(listo ? (e as Error).message : textoErrorConexion(leerError(e)));
      })
      .finally(() => { if (montado.current) setCargando(false); });
  }, [abierta, estado, traer]);

  // Se sigue preguntando mientras el mensaje no llegue a un estado final, hasta 60 s.
  const sigue = desde !== null && Date.now() - desde < TOPE_MS && !sandboxTerminado(estado?.ultimoEstado);
  const polling = usePolling(async () => {
    const e = await traer();
    if (e && sandboxTerminado(e.ultimoEstado)) setDesde(null);
  }, sigue ? POLLING_MS : null, [desde]);

  const tokenOk = !!cuenta?.authTokenGuardado;
  const demo = !!cuenta?.marcaDemoBloqueada;

  const mandar = async (ev: FormEvent) => {
    ev.preventDefault();
    if (enviando) return;
    const destino = to.trim();
    if (!E164.test(destino)) { setError("Poné tu WhatsApp en formato internacional: +5491155443322."); return; }
    setEnviando(true);
    setError("");
    setResultado(null);
    try {
      const l = leerRespuesta<ResultadoPruebaSandbox>(await oficialApi.pruebaSandbox(destino));
      if (!l.ok) {
        setError(textoFalloPruebaSandbox({ ...l, codigoTwilio: l.extra.codigoTwilio }, cuenta?.accountSidEnmascarado));
        return;
      }
      if (l.data && sandboxTerminado(l.data.estado) && ["failed", "undelivered"].includes(String(l.data.estado).toLowerCase())) {
        setError(textoErrorSandbox(null, cuenta?.accountSidEnmascarado));
      }
      setDesde(Date.now());
      setResultado(l.data ?? null);
      await traer();
      // Si la prueba superó un freno, la tarjeta de la marca frenada y el aviso de arriba también lo tienen que decir.
      if (l.data?.frenoSuperado) await recargar();
    } catch (e) {
      const x = leerError(e);
      setError(textoFalloPruebaSandbox({ ...x, codigoTwilio: x.body.codigoTwilio }, cuenta?.accountSidEnmascarado));
    } finally {
      setEnviando(false);
    }
  };

  const copiar = async (clave: string, texto: string) => {
    try { await navigator.clipboard.writeText(texto); setCopiado(clave); setTimeout(() => setCopiado(""), 2000); }
    catch { /* sin portapapeles: la URL igual está a la vista */ }
  };

  const fallo = estado && ["failed", "undelivered"].includes(String(estado.ultimoEstado).toLowerCase());
  const respondio = estado?.ultimoInboundAt && (!estado.ultimaPruebaAt || new Date(estado.ultimoInboundAt) >= new Date(estado.ultimaPruebaAt));

  return (
    <Tarjeta titulo="Prueba con el Sandbox (opcional)" sub="Sirve para probar la conexión antes de tener el número propio. No sirve para campañas."
      right={
        <button type="button" onClick={() => setAbierta((v) => !v)} aria-expanded={abierta}
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900">
          {abierta ? <ChevronDown size={15} /> : <ChevronRight size={15} />} {abierta ? "Ocultar" : "Abrir"}
        </button>
      }>
      {abierta && (
        <div className="space-y-4 text-sm">
          {cargando && !estado && <div className="h-24 rounded-xl bg-slate-100 animate-pulse" aria-busy="true" />}
          {estado && (
            <ol className="list-decimal pl-5 space-y-3 text-slate-700">
              <li>
                En la consola de Twilio, Messaging → Try it out → WhatsApp → Sandbox settings, pegá estas dos direcciones:
                <UrlCopiable etiqueta="When a message comes in" url={estado.urlEntrada} copiado={copiado === "entrada"} onCopiar={() => void copiar("entrada", estado.urlEntrada ?? "")} />
                <UrlCopiable etiqueta="Status callback URL" url={estado.urlEstado} copiado={copiado === "estado"} onCopiar={() => void copiar("estado", estado.urlEstado ?? "")} />
              </li>
              <li>Mandá «join &lt;tu código&gt;» al +1 415 523 8886 ({estado.numeroSandbox || NUMERO_SANDBOX}) desde tu WhatsApp. El código está en esa misma página de la consola. La sesión dura 3 días.</li>
              <li>
                Tocá «Mandarme un mensaje de prueba». Se cobra como un mensaje normal en tu cuenta de Twilio (no del saldo interno de la marca).
              </li>
            </ol>
          )}

          {demo ? (
            <p className="text-slate-600 bg-slate-50 rounded-lg px-3 py-2">La marca de demostración no usa el Sandbox real.</p>
          ) : !tokenOk ? (
            <CredencialesSandbox />
          ) : (
            <form onSubmit={mandar} autoComplete="off" className="flex items-end gap-3 flex-wrap">
              <div>
                <label htmlFor="wa-sandbox-to" className="text-sm font-medium text-slate-700">Tu WhatsApp</label>
                <input id="wa-sandbox-to" value={to} onChange={(e) => { setTo(e.target.value.trim()); setError(""); }} placeholder="+5491155443322" inputMode="tel"
                  className="mt-1 w-48 rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </div>
              <button type="submit" disabled={enviando || !to}
                className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40">
                {enviando ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Mandarme un mensaje de prueba
              </button>
            </form>
          )}

          {error && <p role="alert" className="text-red-600">{error}</p>}
          {estado?.ultimaPruebaAt && (
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              <dt className="text-slate-500">Última prueba</dt><dd className="text-slate-800">{fechaHora(estado.ultimaPruebaAt)} a {estado.destinoEnmascarado || "—"}</dd>
              <dt className="text-slate-500">Estado</dt>
              <dd className={fallo ? "text-red-600" : "text-slate-800"}>
                {ESTADO_SANDBOX[String(estado.ultimoEstado).toLowerCase()] || estado.ultimoEstado || "sin recibo todavía"}
                {estado.ultimoCodigoError ? ` (código ${estado.ultimoCodigoError})` : ""}
                {sigue && <Loader2 size={12} className="inline ml-1.5 animate-spin text-slate-400" />}
              </dd>
              <dt className="text-slate-500">Tu respuesta</dt>
              <dd className={respondio ? "text-emerald-700" : "text-slate-800"}>
                {respondio ? <span className="inline-flex items-center gap-1"><CheckCircle2 size={14} /> Llegó {fechaHora(estado.ultimoInboundAt || undefined)}</span> : "Respondé el mensaje desde tu WhatsApp para probar la entrada."}
              </dd>
            </dl>
          )}
          {fallo && !error && <p className="text-red-600">{textoErrorSandbox(estado?.ultimoCodigoError, cuenta?.accountSidEnmascarado)}</p>}
          <ReanudarTrasPrueba superado={frenoSuperado(cuenta, resultado)} />
          {polling.detenido && (
            <p className="text-xs text-amber-800">Dejamos de preguntar porque el servidor no responde. <button type="button" onClick={polling.reintentar} className="font-semibold hover:underline">Reintentar</button></p>
          )}
          <p className="text-xs text-slate-400 inline-flex items-center gap-1.5"><FlaskConical size={12} /> El Sandbox no usa tu número ni tus plantillas: sólo prueba que Twilio y Deenex se hablan.</p>
        </div>
      )}
    </Tarjeta>
  );
}

/**
 * Para probar con el Sandbox hace falta el token guardado, pero todavía no hay número: se guarda
 * la forma corta `{ accountSid, authToken }` (§A.4.5). El token se vacía del estado al mandarlo.
 */
function CredencialesSandbox() {
  const { recargar } = useCuentaOficial();
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const guardar = async (ev: FormEvent) => {
    ev.preventDefault();
    if (guardando) return;
    const e = validarCredencialesLocal({ accountSid: accountSid.trim(), authToken: authToken.trim() });
    if (e.accountSid || e.authToken) { setError([e.accountSid && `Account SID: ${e.accountSid}`, e.authToken && `Auth Token: ${e.authToken}`].filter(Boolean).join(" ")); return; }
    const c = { accountSid: accountSid.trim(), authToken: authToken.trim() };
    setAuthToken("");
    setGuardando(true);
    setError("");
    try {
      const l = leerRespuesta<CuentaPublica>(await oficialApi.guardarSoloCredenciales(c));
      if (!l.ok) { setError(textoErrorConexion({ ...l, codigoTwilio: l.extra.codigoTwilio })); return; }
      await recargar();
    } catch (err) {
      setError(textoErrorConexion(leerError(err)));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={guardar} autoComplete="off" className="space-y-3 rounded-xl border border-slate-100 p-4">
      <p className="text-slate-600">Para la prueba hace falta guardar el Account SID y el Auth Token (sin número todavía).</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="wa-sandbox-sid" className="text-sm font-medium text-slate-700">Account SID</label>
          <input id="wa-sandbox-sid" value={accountSid} onChange={(e) => { setAccountSid(e.target.value.trim()); setError(""); }} placeholder="AC…" spellCheck={false} autoComplete="off"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
        <div>
          <label htmlFor="wa-sandbox-token" className="text-sm font-medium text-slate-700">Auth Token</label>
          <input id="wa-sandbox-token" type="password" value={authToken} onChange={(e) => { setAuthToken(e.target.value.trim()); setError(""); }} placeholder="32 caracteres" spellCheck={false} autoComplete="off"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
      </div>
      {/* Guardar sólo las credenciales también ata el Account SID al comercio (la misma regla que «Conectar»). */}
      <p role="note" className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2">
        {textoCuentaAtada({ accion: "guardar", comercio: activaApi.nombreActual(), brandId: activaApi.marcaActual() })}
      </p>
      {error && <p role="alert" className="text-red-600">{error}</p>}
      <button type="submit" disabled={guardando || !accountSid || !authToken}
        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
        {guardando && <Loader2 size={15} className="animate-spin" />} Guardar para la prueba
      </button>
    </form>
  );
}

function UrlCopiable({ etiqueta, url, copiado, onCopiar }: { etiqueta: string; url: string | null; copiado: boolean; onCopiar: () => void }) {
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      <span className="text-xs text-slate-500 w-44">{etiqueta}</span>
      <code className="text-xs font-mono bg-slate-50 rounded px-2 py-1 break-all text-slate-800">{url || "falta PUBLIC_BASE_URL en el servidor"}</code>
      {url && (
        <button type="button" onClick={onCopiar} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-0.5 text-xs font-semibold hover:bg-slate-50">
          <Copy size={12} /> {copiado ? "Copiado" : "Copiar"}
        </button>
      )}
    </div>
  );
}

export default PruebaSandbox;
